import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SEO_TEXTES, titreComplet } from '../data/seoTextes';

const SITE_URL = 'https://meteris.fr';
const DEFAULT_IMAGE = 'https://meteris.fr/logo-meteris.png';
// Sans texte donné : ceux de l'accueil, dans le module des textes (une page n'en écrit jamais elle-même).
const ACCUEIL = SEO_TEXTES['/'];

/**
 * Composant SEO unique : injecte title, meta description, canonical, Open Graph, Twitter Card,
 * et un JSON-LD optionnel (BreadcrumbList, FAQPage, Service, etc.). Le JSON-LD Organization /
 * LocalBusiness reste dans public/index.html (statique, présent même sans JS rendu).
 *
 * Usage : les textes viennent de src/data/seoTextes.js (source unique, lue aussi par le pré-rendu et llms.txt) :
 *   <SEO {...SEO_TEXTES['/contact']} path="/contact" />
 *
 * Le title est automatiquement suffixé par "| METERIS" si pas déjà présent (titreComplet, même règle au pré-rendu).
 */
export default function SEO({
  title,
  description = ACCUEIL.description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = titreComplet(title || ACCUEIL.title);

  const url = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  // Une page `noindex` (introuvable, annulation…) n'a pas d'adresse canonique : ni canonical, ni og:url.
  // defer={false} : react-helmet écrit les balises tout de suite, sans attendre requestAnimationFrame, que Chromium
  // suspend dans les onglets en arrière-plan de react-snap (pages pré-rendues sans les balises de la page : constat du
  // 25/10/2026 sur mps et cyber, puis 9 pages de meteris.fr retombées sur le texte de repli).
  return (
    <Helmet defer={false}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {!noindex && <link rel="canonical" href={url} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {!noindex && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
